import styles from './DropDown.module.scss'
import { DropDownProps } from '~types'
import cx from 'classnames'
import { getPlaceholder } from './DropDownConfig'
import DynamicIcon from '../DynamicIcon/DynamicIcon'
import OutsideClickHandler from 'react-outside-click-handler'
import { useDropdown } from 'utils/hooks/useDropDown'
import DropDownMenu from './DropDownMenu'

const DropDown = ({
  options = [],
  dropDownOption,
  placeholder,
  onDropDownChange,
  theme,
  isLoading,
  showPlaceholderInMenu,
  initialSelectedValue = '',
  highlightSelectedValue
}: DropDownProps) => {
  const dropdown = useDropdown({
    options,
    placeholder: placeholder || getPlaceholder(dropDownOption ?? ''),
    onOptionSelect: value => {
      onDropDownChange?.(value, dropDownOption ?? '')
    },
    showPlaceholderInMenu,
    initialSelectedValue
  })

  const dropdownId = `dropdown-${dropDownOption}`
  const menuId = `${dropdownId}-menu`

  return (
    <div
      className={cx(styles.dropdownContainer, styles.alignParent, {
        [styles.disabled]: isLoading,
        [styles[`theme-${theme}`]]: !!theme
      })}
      ref={dropdown.dropdownRef}
      onKeyDown={dropdown.handleKeyDown}
    >
      <OutsideClickHandler onOutsideClick={dropdown.closeDropdown}>
        <button
          ref={dropdown.toggleButtonRef}
          id={dropdownId}
          className={cx(
            styles.dropdownToggle,
            dropdown.isOpen && styles.open,
            (dropdown.selectedValue || dropdown.clearOptionAlreadySelected) && styles.selected
          )}
          onClick={dropdown.handleToggle}
          disabled={isLoading}
          aria-haspopup='listbox'
          aria-expanded={dropdown.isOpen}
          aria-labelledby={`${dropdownId}-label`}
          aria-controls={menuId}
          type='button'
          aria-label={`${dropdown.getPlaceholderText()}, ${
            dropdown.selectedValue ? `selected: ${dropdown.selectedLabel}` : 'no selection'
          }`}
        >
          <span
            className={cx(styles.dropdownSelected, highlightSelectedValue && styles.highlight)}
            id={`${dropdownId}-label`}
          >
            {dropdown.selectedLabel}
          </span>
          <span className={styles.dropdownArrow} aria-hidden='true'>
            <DynamicIcon name='chevron_down' />
          </span>
        </button>

        {dropdown.isOpen && (
          <DropDownMenu
            menuItems={dropdown.menuItems}
            selectedValue={dropdown.selectedValue}
            focusedIndex={dropdown.focusedIndex}
            menuId={menuId}
            dropdownId={dropdownId}
            onOptionClick={dropdown.handleOptionClick}
            onOptionKeyDown={dropdown.handleOptionKeyDown}
            setFocusedIndex={dropdown.setFocusedIndex}
            hasButtonStyles={false}
            parentRef={dropdown.toggleButtonRef}
          />
        )}
      </OutsideClickHandler>

      <div aria-live='polite' aria-atomic='true' className='sr-only'>
        {dropdown.isOpen
          ? `${dropdown.getPlaceholderText()} dropdown opened, ${dropdown.menuItems.length} options available`
          : ''}
        {!dropdown.isOpen && dropdown.selectedValue ? `Selected: ${dropdown.selectedLabel}` : ''}
      </div>
    </div>
  )
}

export default DropDown
